import { useEffect, useState, useCallback, useMemo } from "react";
import StepWrapper from "@/components/steps/StepWrapper";
import FloatingInput from "@/components/ui/FloatingInput";
import UnitToggle from "@/components/ui/UnitToggle";
import ErrorMessage from "@/components/ui/ErrorMessage";
import Button from "@/components/ui/Button";
import useQuizStore from "@/store/useQuizStore";
import {
  validateAge,
  validateHeightMetric,
  validateHeightImperial,
  validateWeightMetric,
  validateWeightImperial,
  validateEmail,
  getAgeError,
  getHeightError,
  getWeightError,
  getEmailError,
} from "@/utils/validation";
import { useKlaviyoLead } from "@/utils/useKlaviyoLead";

const FormStep = ({ step }) => {
  const { formType, title, subtitle, buttonText = "Continue" } = step;

  const {
    quizData,
    preferredUnit,
    updateFormField,
    setPreferredUnit,
    setFormValidity,
    goToNext,
  } = useQuizStore();

  const { createLead, isProviderReady } = useKlaviyoLead();

  const [error, setError] = useState("");
  const [showError, setShowError] = useState(false);
  const getFieldValue = useCallback(
    (field) => quizData[field] || "",
    [quizData],
  );

  const isValid = useMemo(() => {
    switch (formType) {
      case "age":
        return validateAge(getFieldValue("age"));
      case "height":
        return preferredUnit === "metric"
          ? validateHeightMetric(getFieldValue("heightCm"))
          : validateHeightImperial(
              getFieldValue("heightFt"),
              getFieldValue("heightIn"),
            );
      case "weight":
        return preferredUnit === "metric"
          ? validateWeightMetric(
              getFieldValue("currentWeightKg"),
              getFieldValue("goalWeightKg"),
            )
          : validateWeightImperial(
              getFieldValue("currentWeightLb"),
              getFieldValue("goalWeightLb"),
            );
      case "email":
        return validateEmail(getFieldValue("email"));
      default:
        return false;
    }
  }, [formType, preferredUnit, getFieldValue]);

  useEffect(() => {
    setFormValidity(isValid);
  }, [isValid, setFormValidity]);

  const getValidationError = () => {
    switch (formType) {
      case "age":
        return getAgeError();
      case "height":
        return getHeightError();
      case "weight":
        if (preferredUnit === "metric") {
          return getWeightError(
            getFieldValue("currentWeightKg"),
            getFieldValue("goalWeightKg"),
            "metric",
          );
        } else {
          return getWeightError(
            getFieldValue("currentWeightLb"),
            getFieldValue("goalWeightLb"),
            "imperial",
          );
        }
      case "email":
        return getEmailError();
      default:
        return "Invalid input";
    }
  };

  const handleUnitToggle = (newUnit) => {
    setShowError(false);
    setPreferredUnit(newUnit);
  };

  const handleBlur = (e) => {
    if (
      e.relatedTarget &&
      (e.relatedTarget.closest(".unit-toggle-container") ||
        e.relatedTarget.closest(".step-input-group"))
    ) {
      return;
    }

    if (!isValid) {
      setError(getValidationError());
      setShowError(true);
    }
  };

  const handleSubmit = async () => {
    if (!isValid) {
      setError(getValidationError());
      setShowError(true);
      return;
    }
    setShowError(false);

    if (formType === "email" && isProviderReady) {
      await createLead();
    }
    goToNext();
  };

  const handleInputChange = () => {
    setShowError(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  useEffect(() => {
    const inputMap = {
      age: "ageInput",
      height: preferredUnit === "metric" ? "heightCm" : "heightFt",
      weight:
        preferredUnit === "metric" ? "currentWeightKg" : "currentWeightLb",
      email: "emailInput",
    };

    const targetId = inputMap[formType];

    if (targetId) {
      const timer = setTimeout(() => {
        const input = document.getElementById(targetId);
        if (input) {
          input.focus();
          input.click();
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [formType, preferredUnit]);

  const renderForm = () => {
    switch (formType) {
      case "age":
        return (
          <>
            <FloatingInput
              id="ageInput"
              type="number"
              label="Enter your age"
              value={getFieldValue("age")}
              onChange={(e) => {
                updateFormField("age", e.target.value);
                handleInputChange();
              }}
              onBlur={handleBlur}
              error={showError}
              maxLength={2}
              inputMode="numeric"
              pattern="[0-9]*"
            />
            <ErrorMessage message={error} show={showError} />
          </>
        );

      case "height":
        return (
          <>
            <div className="unit-toggle-container">
              <UnitToggle value={preferredUnit} onChange={handleUnitToggle} />
            </div>

            {preferredUnit === "metric" ? (
              <FloatingInput
                id="heightCm"
                type="number"
                label="Height in cm"
                value={getFieldValue("heightCm")}
                onChange={(e) => {
                  updateFormField("heightCm", e.target.value);
                  handleInputChange();
                }}
                onBlur={handleBlur}
                error={showError}
                maxLength={3}
                inputMode="numeric"
                pattern="[0-9]*"
              />
            ) : (
              <div className="grid grid-cols-2 gap-2 md:gap-2 step-input-group">
                <FloatingInput
                  id="heightFt"
                  type="number"
                  label="Feet"
                  value={getFieldValue("heightFt")}
                  onChange={(e) => {
                    updateFormField("heightFt", e.target.value);
                    handleInputChange();
                  }}
                  onBlur={handleBlur}
                  error={showError}
                  maxLength={1}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                <FloatingInput
                  id="heightIn"
                  type="number"
                  label="Inches"
                  value={getFieldValue("heightIn")}
                  onChange={(e) => {
                    updateFormField("heightIn", e.target.value);
                    handleInputChange();
                  }}
                  onBlur={handleBlur}
                  error={showError}
                  maxLength={2}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </div>
            )}
            <ErrorMessage message={error} show={showError} />
          </>
        );

      case "weight":
        const unit = preferredUnit || "metric";

        return (
          <>
            <div className="unit-toggle-container">
              <UnitToggle value={preferredUnit} onChange={handleUnitToggle} />
            </div>

            {unit === "metric" ? (
              <div className="flex flex-col gap-2.5 md:gap-4 step-input-group">
                <FloatingInput
                  id="currentWeightKg"
                  type="number"
                  label="Current weight in kg"
                  value={getFieldValue("currentWeightKg")}
                  onChange={(e) => {
                    updateFormField("currentWeightKg", e.target.value);
                    handleInputChange();
                  }}
                  onBlur={handleBlur}
                  error={showError}
                  maxLength={3}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                <FloatingInput
                  id="goalWeightKg"
                  type="number"
                  label="Goal weight in kg"
                  value={getFieldValue("goalWeightKg")}
                  onChange={(e) => {
                    updateFormField("goalWeightKg", e.target.value);
                    handleInputChange();
                  }}
                  onBlur={handleBlur}
                  error={showError}
                  maxLength={3}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </div>
            ) : (
              <div className="flex flex-col gap-2.5 md:gap-4 step-input-group">
                <FloatingInput
                  id="currentWeightLb"
                  type="number"
                  label="Current weight in lbs"
                  value={getFieldValue("currentWeightLb")}
                  onChange={(e) => {
                    updateFormField("currentWeightLb", e.target.value);
                    handleInputChange();
                  }}
                  onBlur={handleBlur}
                  error={showError}
                  maxLength={3}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                <FloatingInput
                  id="goalWeightLb"
                  type="number"
                  label="Goal weight in lbs"
                  value={getFieldValue("goalWeightLb")}
                  onChange={(e) => {
                    updateFormField("goalWeightLb", e.target.value);
                    handleInputChange();
                  }}
                  onBlur={handleBlur}
                  error={showError}
                  maxLength={3}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </div>
            )}
            <ErrorMessage message={error} show={showError} />
            <p className="text-[0.75rem] md:text-[0.8rem] text-gray leading-normal mt-1 text-center">
              The results of this survey are not guaranteed and Naori users are
              expected to lose {unit === "metric" ? "0.5-1 kg" : "1–2 lb"} per
              week if they adopt a healthy lifestyle.
            </p>
          </>
        );

      case "email":
        return (
          <>
            <FloatingInput
              id="emailInput"
              type="email"
              label="Your email address"
              value={getFieldValue("email")}
              onChange={(e) => {
                updateFormField("email", e.target.value);
                handleInputChange();
              }}
              onBlur={handleBlur}
              error={showError}
              inputMode="email"
            />
            <ErrorMessage message={error} show={showError} />
          </>
        );

      default:
        return null;
    }
  };

  return (
    <StepWrapper title={title} subtitle={subtitle} noPadding>
      <div onKeyDown={handleKeyDown}>{renderForm()}</div>
      <div className="pt-4 md:pt-6">
        <Button onClick={handleSubmit} disabled={!isValid}>
          {buttonText}
        </Button>
      </div>
    </StepWrapper>
  );
};

export default FormStep;
